import useApi from '@/hooks/useApi';
import { router } from '@inertiajs/react';
import { Search } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SearchResult {
    id: number;
    title: string;
    type: 'course' | 'class';
    course_title?: string;
    url: string;
}

const HeaderSearch: React.FC = () => {
    const { t } = useTranslation();
    const api = useApi();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const debounceRef = useRef<number | null>(null);

    useEffect(() => {
        if (debounceRef.current) {
            window.clearTimeout(debounceRef.current);
        }

        const trimmedQuery = query.trim();
        if (!trimmedQuery) {
            setResults([]);
            setIsOpen(false);
            setHasSearched(false);
            return;
        }

        debounceRef.current = window.setTimeout(() => {
            setIsLoading(true);
            setHasSearched(true);

            api.get('/courses/search/results/', {
                params: { q: trimmedQuery, per_page: 5 },
            })
                .then((response) => {
                    setResults(response.data.results || []);
                    setIsOpen(true);
                })
                .catch(() => {
                    setResults([]);
                    setIsOpen(true);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }, 300);

        return () => {
            if (debounceRef.current) {
                window.clearTimeout(debounceRef.current);
            }
        };
    }, [api, query]);

    const handleViewAll = () => {
        const trimmedQuery = query.trim();
        if (!trimmedQuery) {
            return;
        }
        router.get(`/courses/search/?q=${encodeURIComponent(trimmedQuery)}`);
        setIsOpen(false);
    };

    const handleResultClick = (url: string) => {
        router.get(url);
        setIsOpen(false);
    };

    return (
        <div className="relative">
            <Search
                className="absolute top-1/2 left-3 -translate-y-1/2 transform text-gray-400"
                size={18}
            />
            <input
                type="text"
                placeholder={t('search.placeholder')}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onFocus={() => {
                    if (results.length > 0 || hasSearched) {
                        setIsOpen(true);
                    }
                }}
                className="input w-72"
            />
            {isOpen && (
                <div className="absolute right-0 z-20 mt-2 w-80 rounded-lg border border-gray-200 bg-white shadow-lg">
                    <div className="max-h-80 overflow-y-auto p-2">
                        {isLoading && (
                            <div className="px-3 py-2 text-sm text-gray-500">
                                {t('search.loading')}
                            </div>
                        )}
                        {!isLoading && results.length === 0 && hasSearched && (
                            <div className="px-3 py-2 text-sm text-gray-500">
                                {t('search.noResults')}
                            </div>
                        )}
                        {!isLoading &&
                            results.map((result) => (
                                <button
                                    key={`${result.type}-${result.id}`}
                                    type="button"
                                    onClick={() =>
                                        handleResultClick(result.url)
                                    }
                                    className="flex w-full flex-col gap-1 rounded-md px-3 py-2 text-left transition hover:bg-gray-100"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-900">
                                            {result.title}
                                        </span>
                                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                                            {result.type === 'course'
                                                ? t('search.types.course')
                                                : t('search.types.class')}
                                        </span>
                                    </div>
                                    {result.type === 'class' &&
                                        result.course_title && (
                                            <span className="text-xs text-gray-500">
                                                {result.course_title}
                                            </span>
                                        )}
                                </button>
                            ))}
                    </div>
                    <div className="border-t border-gray-100 p-2">
                        <button
                            type="button"
                            onClick={handleViewAll}
                            className="w-full rounded-md px-3 py-2 text-left text-sm font-medium text-theme-primary-text transition hover:bg-theme-light"
                        >
                            {t('search.viewAll')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HeaderSearch;
